import { type FC, type HTMLAttributes } from "react";

import classnames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { useGetProfile } from "@jti/rest-api";
import { useSelector } from "@jti/store/reduxHooks";
import { selectIsZeroValueOrder } from "@jti/store/selectors/selectIsZeroValueOrder";
import { useFormContext, useTranslations } from "@jti/ui";

import { useFreeTrial } from "@checkout/hooks";
import useDelivery from "@checkout/hooks/useDelivery";

import styles from "./DeliveryFormFooter.module.scss";

type DeliveryFormFooterProps = {
  isSubmitDisabled: boolean;
  onSubmitButtonClick?: () => void;
  className?: string;
} & HTMLAttributes<HTMLDivElement>;

export const DeliveryFormFooter: FC<DeliveryFormFooterProps> = ({
  isSubmitDisabled,
  onSubmitButtonClick,
  className,
  ...restProps
}) => {
  const { __ } = useTranslations();

  const { shippingMethods, setShippingMethodsOnCartPending, placingOrder, priceFetchStatus } = useDelivery();
  const { isLoading: isGetProfileLoading } = useGetProfile();
  const { isFreeTrial } = useFreeTrial();

  const isZeroValueOrder = useSelector(selectIsZeroValueOrder(isFreeTrial));
  const formContext = useFormContext();
  const { isSubmitting } = formContext.formState;

  const isSubmitButtonPending =
    isSubmitting ||
    placingOrder ||
    priceFetchStatus === "pending" ||
    shippingMethods.fetchStatus === "pending" ||
    isGetProfileLoading ||
    setShippingMethodsOnCartPending;

  return (
    <div className={classnames([styles.wrapper, className])} {...restProps}>
      <Button
        variant="primary"
        pending={isSubmitButtonPending}
        disabled={isSubmitDisabled}
        type="submit"
        onClick={onSubmitButtonClick}
      >
        {isZeroValueOrder ? __("cart.order.payment.finalize") : __("cart.checkout.place_order.button")}
      </Button>

      <div className={styles.text} data-testid="place-order-warning-text">
        {__("cart.checkout.place_order.text")}
        <span className={styles.notText}>{` ${__("cart.checkout.place_order.text.not")} `}</span>
        {__("cart.checkout.place_order.text_next")}
        <div className={styles.subText}>{__("cart.checkout.place_order.sub_text")}</div>
      </div>
    </div>
  );
};
